import React, { useEffect, useState } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Divider } from 'primereact/divider';
import MetricDetails from './MetricDetails';
import { useGetMonthlyMetricsSummaryQuery } from '../../pages/metricdashboard/metricDashboardSlice';
import trendDown from '../../assets/trendDown.svg';
import trendUp from '../../assets/trendUp.svg';
import finalscore from '../../assets/finalscore.svg';
import { metricValueFormat } from '../../utils/metricValueFormat';

interface CalculationMetricCardProps {
    seletedRowData: any;
    inputMonth?: number;
    inputYear?: number;
    scorecardVersionID?: number | string;
}

const CompositeFormula = (metrics: any) => {
    if (!metrics?.length) return null;
    const weight = (100 / metrics.length).toFixed(2);
    const formulaParts = metrics.map((m: any) => `(${m.label}% × ${weight}%)`);

    return (
        <div>
            <strong>
                Composite Score Formula = <div style={{ fontWeight: 400 }}>{formulaParts.join(' + ')}</div>
            </strong>
        </div>
    );
};

const CompositeScore = (metrics: any) => {
    if (!metrics?.length) return null;

    const weight = 100 / metrics.length;
    const formulaParts = metrics.map(
        (m: any) => `(${metricValueFormat(m.percent_to_target, m.metric_type_name)} × ${weight.toFixed(2)}%)`
    );
    const calcParts = metrics.map((m: any) => {
        const percentValue = m.percent_to_target * weight;
        return `${percentValue.toFixed(2)}%`;
    });
    // const finalScore = calcParts.reduce((sum: number, val: any) => sum + parseFloat(val), 0).toFixed(2);

    return (
        <div>
            <div style={{ fontWeight: 400 }}>
                <strong>Final Comp. Score = </strong>
                {formulaParts.join(' + ')}
                <br/><br/>
                <strong>Final Comp. Score = </strong>
                {calcParts.join(' + ')}
                {/* = <strong>{finalScore}</strong> */}
            </div>
        </div>
    );
};

export const CalculationMetricCard: React.FC<CalculationMetricCardProps> = ({
    seletedRowData,
    inputMonth,
    inputYear,
    scorecardVersionID,
}) => {
    const [monthlyMetricsReported, setMonthlyMetricsReported] = useState<any>();
    const { data: monthlyMetricsData } = useGetMonthlyMetricsSummaryQuery(
        {
            employeenumber: parseInt(seletedRowData?.employee_number, 10),
            year: inputYear,
            month: inputMonth,
            scorecardVersionId: scorecardVersionID,
            scorecardRoleId: 1,
        },
        {
            skip: !inputYear || !inputMonth || !scorecardVersionID || !seletedRowData?.employee_number,
        }
    );

    useEffect(() => {
        if (monthlyMetricsData?.data?.length > 0) {
            const notOmittedMetrics = monthlyMetricsData?.data?.filter(
                (metric: any) => metric.metric_omitted === false
            );
            setMonthlyMetricsReported(notOmittedMetrics);
        }
    }, [monthlyMetricsData]);

    return (
        <div className="flex flex-column gap-2">
            <Divider />
            <Accordion id="AscendingMetrics" style={{ margin: '20px', borderRadius: '10px' }}>
                <AccordionTab
                    header={
                        <div className="flex align-items-center gap-2">
                            <img src={trendUp} alt="trend" className="trend-icon" />
                            <span> Ascending Metrics (Higher is Better)</span>
                        </div>
                    }
                >
                    <div
                        className="mt-2 p-2 border-1 border-round border-200 bg-gray-50"
                        style={{ fontWeight: 400 }}
                    >
                        <div>
                            <p style={{ marginBottom: '12px', color: '#374151' }}>
                                For metrics where higher values are better (like sales or quality scores):
                                <br />
                                When minimum equals 0: Percent to Target ={' '}
                                <strong>Actual Value ÷ Target Value</strong>
                                <br />
                                When minimum is greater than 0: Percent to Target ={' '}
                                <strong>(Actual Value - Minimum) ÷ (Target - Minimum)</strong>
                            </p>

                            <div
                                style={{
                                    backgroundColor: '#E7F3FF',
                                    borderRadius: '10px',
                                    border: '2px solid #1A5AA5',
                                }}
                                className="p-2 mt-2"
                            >
                                <p>
                                    <strong>Example:</strong>
                                    <br />
                                    If your target is 90 and minimum is 60, achieving 85 means you&apos;ve
                                    reached 83.3% of your target: (85-60) ÷ (90-60) = 83.3%
                                </p>
                            </div>
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>
            {monthlyMetricsData?.data?.length > 0 ? (
                monthlyMetricsData?.data
                ?.filter((metric: any) => metric.metric_direction_id === 1)
                .map((metric: any, i: number) => {
                    return <MetricDetails key={metric.metric_id ?? i} metric={metric} index={i} />;
                })
            ) : (
                <div className="no-data-wrapper">
                    <div className="no-data-inner">
                        <div className="no-data-text">No Data Available</div>
                    </div>
                </div>
            )}
            <Accordion id="AscendingMetrics" style={{ margin: '20px', borderRadius: '10px' }}>
                <AccordionTab
                    header={
                        <div className="flex align-items-center gap-2">
                            <img src={trendDown} alt="trend" className="trend-icon" />
                            <span> Descending Metrics (Lower is Better)</span>
                        </div>
                    }
                >
                    <div
                        className="mt-2 p-2 border-1 border-round border-200 bg-gray-50"
                        style={{ fontWeight: 400 }}
                    >
                        <div>
                            <p style={{ marginBottom: '12px', color: '#374151' }}>
                                For metrics where lower values are better (like call handle time):
                                <br />
                                Percent to Target ={' '}
                                <strong>
                                    (Maximum Acceptable - Actual Value) ÷ (Maximum Acceptable - Target)
                                </strong>
                            </p>

                            <div
                                style={{
                                    backgroundColor: '#E7F3FF',
                                    borderRadius: '10px',
                                    border: '2px solid #1A5AA5',
                                }}
                                className="p-2 mt-2"
                            >
                                <p>
                                    <strong>Example:</strong>
                                    <br />
                                    If your target is 10 seconds and maximum acceptable is 15 seconds,
                                    achieving 12 seconds means you&apos;ve reached 60% of your target: (15-12)
                                    ÷ (15-10) = 60%
                                </p>
                            </div>
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>
            {monthlyMetricsData?.data?.length > 0 ? (
                monthlyMetricsData?.data
                ?.filter((metric: any) => metric.metric_direction_id === 2)
                .map((metric: any, i: number) => {
                    return <MetricDetails key={metric.metric_id ?? i} metric={metric} index={i} />;
                })
            ) : (
                <div className="no-data-wrapper">
                    <div className="no-data-inner">
                        <div className="no-data-text">No Data Available</div>
                    </div>
                </div>
            )}

            <Divider />
            <Accordion id="AscendingMetrics" style={{ margin: '20px', borderRadius: '10px' }}>
                <AccordionTab
                    header={
                        <div className="flex align-items-center gap-2">
                            <img src={finalscore} alt="trend" className="trend-icon" />
                            <span> Final Composite Score Calculation</span>
                        </div>
                    }
                >
                    <div className="mt-2 p-2 border-1 border-round border-200 bg-gray-50">
                        <div>
                            <p style={{ marginBottom: '12px', color: '#374151' }}>
                                {CompositeFormula(monthlyMetricsReported ?? [])}
                                <br />
                                <div
                                    style={{
                                        backgroundColor: '#EBFFE7',
                                        borderRadius: '10px',
                                        border: '2px solid #2AA154',
                                    }}
                                    className="p-2 mt-2"
                                >
                                    <p>
                                        <strong>Example:</strong>
                                        <br />
                                        Each metric is equally weighted at 20% (100% ÷ 5 metrics). Percent to
                                        target values can exceed 100% when actual performance exceeds the
                                        target.
                                    </p>
                                </div>
                                <br />
                                <strong>Composite Score Calculation:</strong>
                                <br />
                                {CompositeScore(monthlyMetricsReported ?? [])}
                                <br />
                                <strong>
                                    Final Comp. Score ={' '}
                                    <span style={{ color: '#1A5AA5' }}>
                                        {metricValueFormat(seletedRowData?.composite_score, 'Percentage')}
                                    </span>
                                </strong>
                            </p>
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>
        </div>
    );
};
